import { useEffect, useRef, useState } from "react";
import { UploadCloud, Loader2, Trash2, CheckCircle2, FileText, Film, Image as ImageIcon, Type } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { resolveMediaUrl } from "@/components/FileUpload";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const FONT_EXT = ["ttf", "otf", "woff", "woff2"];
const CAT_OPTIONS = [
  { key: "logo", label: "Logo/Grafik" },
  { key: "font", label: "Schrift" },
  { key: "video", label: "Video-Asset" },
  { key: "preset", label: "Preset/Vorlage" },
];

function detectCategory(fileName, kind) {
  const ext = (fileName.split(".").pop() || "").toLowerCase();
  if (FONT_EXT.includes(ext)) return "font";
  if (kind === "video") return "video";
  if (kind === "image") return "logo";
  return "preset";
}

function cleanName(fileName) {
  return fileName.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ").trim();
}

export function BrandBulkUpload({ open, onOpenChange, accountKey, onDone }) {
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [videoCats, setVideoCats] = useState([]);
  const inputRef = useRef(null);
  const patch = (id, upd) => setItems((list) => list.map((it) => (it.id === id ? { ...it, ...upd } : it)));

  useEffect(() => {
    if (open)
      api.get("/brand/video-categories", { params: { account: accountKey } })
        .then(({ data }) => setVideoCats([...data.defaults, ...data.custom.map((c) => c.name)]))
        .catch(() => {});
  }, [open, accountKey]);

  const addFiles = (files) => {
    const news = Array.from(files).map((file) => ({
      id: `${file.name}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      file, name: cleanName(file.name), fileName: file.name,
      description: "", status: "uploading", url: "", kind: "", category: "preset", subcategory: "",
    }));
    setItems((l) => [...l, ...news]);
    news.forEach(async (it) => {
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        fd.append("account_key", accountKey);
        fd.append("folder_title", "Markenmaterial");
        const { data } = await api.post("/upload", fd);
        patch(it.id, { status: "ready", url: data.url, kind: data.kind, category: detectCategory(it.fileName, data.kind) });
      } catch (e) {
        patch(it.id, { status: "error", error: e.response?.data?.detail || "Upload fehlgeschlagen" });
      }
    });
  };

  const saveAll = async () => {
    const ready = items.filter((it) => it.status === "ready");
    if (ready.some((it) => !it.name.trim())) return toast.error("Bitte allen Dateien einen Namen geben");
    if (!ready.length) return toast.error("Keine fertig hochgeladenen Dateien");
    setSaving(true);
    let ok = 0;
    for (const it of ready) {
      try {
        await api.post("/brand/assets", {
          account_key: accountKey, category: it.category, name: it.name,
          description: it.description, media_url: it.url, file_kind: it.kind,
        });
        patch(it.id, { status: "saved" });
        ok += 1;
      } catch (e) {
        patch(it.id, { status: "error", error: e.response?.data?.detail || "Speichern fehlgeschlagen" });
      }
    }
    setSaving(false);
    if (ok) {
      toast.success(`${ok} Asset${ok === 1 ? "" : "s"} gespeichert ✨`);
      setItems((l) => l.filter((it) => it.status !== "saved"));
      if (!items.some((it) => it.status === "error")) {
        onOpenChange(false);
      }
      onDone();
    }
  };

  const uploading = items.filter((i) => i.status === "uploading").length;
  const readyCount = items.filter((i) => i.status === "ready").length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!saving) { onOpenChange(o); if (!o) setItems([]); } }}>
      <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-head">Mehrere Assets hochladen</DialogTitle>
          <DialogDescription>Alle Dateien auf einmal hochladen, danach benennen — Kategorie wird automatisch erkannt.</DialogDescription>
        </DialogHeader>

        <button
          data-testid="bulk-dropzone"
          onClick={() => inputRef.current?.click()}
          className="w-full rounded-xl border-2 border-dashed border-neutral-300 hover:border-fuchsia-400 hover:bg-fuchsia-50/40 transition p-8 text-center"
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        >
          <UploadCloud className="h-8 w-8 text-neutral-400 mx-auto" />
          <p className="text-sm font-medium text-neutral-700 mt-2">Dateien hierher ziehen oder klicken</p>
          <p className="text-[12px] text-neutral-400 mt-0.5">Bilder, Videos, Schriften (ttf/otf/woff), PDFs, LUTs, Audio — beliebig viele auf einmal</p>
        </button>
        <input ref={inputRef} data-testid="bulk-file-input" type="file" multiple className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />

        {items.length > 0 && (
          <div className="space-y-2">
            {items.some((it) => it.category === "video" && it.status !== "uploading") && (
              <div className="flex items-center gap-2 bg-violet-50 border border-violet-200 rounded-lg px-3 py-2">
                <Film className="h-4 w-4 text-violet-600 shrink-0" />
                <span className="text-[12px] font-semibold text-violet-800">Video-Kategorie für alle Videos:</span>
                <select
                  data-testid="bulk-videocat-all"
                  defaultValue=""
                  onChange={(e) => {
                    const v = e.target.value;
                    setItems((l) => l.map((it) => (it.category === "video" ? { ...it, subcategory: v } : it)));
                  }}
                  className="h-8 px-2 rounded-md border border-violet-200 text-sm bg-white flex-1 min-w-0"
                >
                  <option value="">Sonstiges</option>
                  {videoCats.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            )}
            {items.map((it) => (
              <div key={it.id} data-testid="bulk-item-row" className="flex items-center gap-3 border border-neutral-200 rounded-lg p-2.5">
                <div className="h-11 w-11 rounded-lg bg-neutral-100 flex items-center justify-center overflow-hidden shrink-0">
                  {it.status === "uploading" ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />
                    : it.kind === "image" && it.url ? <img src={resolveMediaUrl(it.url)} alt="" className="h-full w-full object-contain" />
                    : it.kind === "video" ? <Film className="h-4 w-4 text-neutral-500" />
                    : it.category === "font" ? <Type className="h-4 w-4 text-neutral-500" />
                    : <FileText className="h-4 w-4 text-neutral-500" />}
                </div>
                <div className="flex-1 min-w-0 grid sm:grid-cols-2 gap-2">
                  <input
                    data-testid="bulk-item-name"
                    value={it.name}
                    onChange={(e) => patch(it.id, { name: e.target.value })}
                    disabled={it.status === "uploading"}
                    placeholder="Name des Assets"
                    className="h-9 px-2.5 rounded-md border border-neutral-200 text-sm min-w-0"
                  />
                  <div className="flex items-center gap-2 min-w-0">
                    <select
                      data-testid="bulk-item-category"
                      value={it.category}
                      onChange={(e) => patch(it.id, { category: e.target.value })}
                      disabled={it.status === "uploading"}
                      className="h-9 px-2 rounded-md border border-neutral-200 text-sm bg-white flex-1 min-w-0"
                    >
                      {CAT_OPTIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    </select>
                    {it.category === "video" && (
                      <select
                        data-testid="bulk-item-subcategory"
                        value={it.subcategory}
                        onChange={(e) => patch(it.id, { subcategory: e.target.value })}
                        disabled={it.status === "uploading"}
                        className="h-9 px-2 rounded-md border border-neutral-200 text-sm bg-white flex-1 min-w-0"
                      >
                        <option value="">Sonstiges</option>
                        {videoCats.map((n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    )}
                    {it.status === "saved" && <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />}
                    {it.status === "error" && <span className="text-[11px] text-rose-600 shrink-0" title={it.error}>Fehler</span>}
                    <button onClick={() => setItems((l) => l.filter((x) => x.id !== it.id))} className="text-neutral-300 hover:text-rose-500 shrink-0">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="items-center gap-3">
          {uploading > 0 && <span className="text-[12px] text-neutral-400 mr-auto">{uploading} Datei(en) laden hoch…</span>}
          <button
            data-testid="bulk-save-all"
            onClick={saveAll}
            disabled={saving || readyCount === 0}
            className="inline-flex items-center gap-2 bg-neutral-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg hover:bg-neutral-800 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
            {readyCount > 0 ? `${readyCount} Asset${readyCount === 1 ? "" : "s"} speichern` : "Alle speichern"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
